import { motion } from 'framer-motion';
import { Button } from '../../components/common';
import { usePageTitle } from '../../hooks/usePageTitle';

// Fotos das aulas online
const CLASS_PHOTOS = [
  'https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=600',
  'https://images.unsplash.com/photo-1609234656388-0ff363383899?w=600',
  'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=600',
];

// Turmas disponíveis
const CLASSES = [
  {
    id: 1,
    name: 'TURMA A1',
    teacher: 'Teacher Tallyne',
    day: 'Segunda-feira',
    time: '19:30',
    spotsTotal: 3,
    spotsTaken: 0,
    price: 250,
  },
  {
    id: 2,
    name: 'TURMA A1',
    teacher: 'Teacher Elienai',
    day: 'Quarta-feira',
    time: '13:00',
    spotsTotal: 3,
    spotsTaken: 0,
    price: 250,
  },
];

export default function Classes() {
  usePageTitle('Turmas');
  
  return (
    <div className="min-h-screen bg-sand-50">
      {/* Hero Header */}
      <header className="relative bg-earth-900 pt-24 sm:pt-28 pb-12 sm:pb-16 overflow-hidden">
        {/* Background com fotos */}
        <div className="absolute inset-0 opacity-20">
          <div className="grid grid-cols-3 h-full">
            {CLASS_PHOTOS.map((photo, idx) => (
              <img key={idx} src={photo} alt="" className="w-full h-full object-cover" />
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-earth-900/80 via-earth-900/90 to-earth-900" />
        
        <div className="container-custom relative z-10">
          {/* Badge de urgência */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold mb-4 sm:mb-6"
          >
            <i className="ri-fire-fill animate-pulse"></i>
            Últimas vagas disponíveis!
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 sm:mb-4"
          >
            Turmas de Inglês<br />
            <span className="text-olive-400">Online ao Vivo</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-sand-300 text-base sm:text-lg max-w-xl mb-6 sm:mb-8"
          >
            Aulas em grupos pequenos com professores experientes.
            Aprenda inglês de verdade, conversando.
          </motion.p>

          {/* Badges de benefícios */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-2 sm:gap-3"
          >
            {[
              { icon: 'ri-group-line', text: 'Grupos pequenos' },
              { icon: 'ri-vidicon-line', text: 'Via Meet' },
              { icon: 'ri-calendar-check-line', text: '1x por semana' },
              { icon: 'ri-time-line', text: '1h por aula' },
            ].map((item) => (
              <span 
                key={item.text}
                className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sand-200 text-xs sm:text-sm"
              >
                <i className={`${item.icon} text-olive-400`}></i>
                {item.text}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      <main className="container-custom py-8 sm:py-12">
        {/* Turmas */}
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {CLASSES.map((c) => (
              <ClassCard key={c.id} classItem={c} />
            ))}
          </div>

          {/* Info Rodapé */}
          <div className="mt-10 p-5 sm:p-6 bg-olive-50 rounded-2xl border border-olive-200">
            <div className="flex items-center gap-2 justify-center mb-4">
              <i className="ri-shield-check-line text-olive-600 text-xl"></i>
              <span className="font-semibold text-earth-800">Por que escolher nossas turmas?</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center">
              {[
                { icon: 'ri-group-line', text: 'Grupos pequenos' },
                { icon: 'ri-vidicon-line', text: 'Aulas via Meet' },
                { icon: 'ri-calendar-line', text: '1x por semana' },
                { icon: 'ri-time-line', text: '1h por aula' },
              ].map((item) => (
                <div key={item.text} className="flex flex-col items-center gap-1.5 sm:gap-2">
                  <i className={`${item.icon} text-xl sm:text-2xl text-olive-600`} />
                  <span className="text-xs sm:text-sm text-earth-600">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* CTA */}
      <section className="bg-earth-800 py-10">
        <div className="container-custom text-center">
          <p className="text-sand-200 mb-4">Ficou com dúvidas? Fale com a gente!</p>
          <Button
            href="https://wa.me/5581997160139?text=Olá! Quero saber mais sobre as turmas de inglês em grupo da Beyond Class."
            className="bg-olive-500 hover:bg-olive-400 text-white"
            icon="ri-whatsapp-line"
          >
            Chamar no WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
}

// Componente do Card
function ClassCard({ classItem }: { classItem: typeof CLASSES[0] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-earth-100 hover:border-olive-300 transition-colors"
    >
      {/* Nome da Turma + Vagas */}
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <h3 className="text-lg sm:text-xl font-bold text-earth-800">{classItem.name}</h3>
        <span className="bg-orange-100 text-orange-600 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1">
          <i className="ri-fire-fill text-[10px] sm:text-xs"></i>
          Últimas vagas
        </span>
      </div>

      {/* Info da Turma */}
      <div className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-6">
        <div className="flex items-center gap-2.5 sm:gap-3 text-earth-600">
          <i className="ri-calendar-line text-olive-600 text-base sm:text-lg"></i>
          <span className="font-medium text-sm sm:text-base">{classItem.day}</span>
        </div>
        <div className="flex items-center gap-2.5 sm:gap-3 text-earth-600">
          <i className="ri-time-line text-olive-600 text-base sm:text-lg"></i>
          <span className="font-medium text-sm sm:text-base">{classItem.time}</span>
        </div>
        <div className="flex items-center gap-2.5 sm:gap-3 text-earth-600">
          <i className="ri-user-line text-olive-600 text-base sm:text-lg"></i>
          <span className="font-medium text-sm sm:text-base">{classItem.teacher}</span>
        </div>
      </div>

      {/* Indicador de vagas */}
      <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-5">
        {Array.from({ length: classItem.spotsTotal }).map((_, i) => (
          <div
            key={i}
            className="flex-1 h-1.5 sm:h-2 rounded-full bg-earth-200"
          />
        ))}
      </div>

      {/* Preço + CTA */}
      <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-earth-100">
        <div>
          <span className="text-xl sm:text-2xl font-bold text-earth-800">R$ {classItem.price}</span>
          <span className="text-earth-400 text-xs sm:text-sm">/mês</span>
        </div>
        <a
          href={`https://wa.me/5581997160139?text=Olá! Tenho interesse na ${classItem.name} - ${classItem.day} às ${classItem.time} com ${classItem.teacher}. Gostaria de reservar uma vaga!`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-olive-500 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-olive-600 transition-colors"
        >
          <i className="ri-whatsapp-line text-base sm:text-lg" />
          Reservar
        </a>
      </div>
    </motion.div>
  );
}
